import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { ContestStatusFilter } from '~/domains/contest/domains/services/enums/contest-status-filter.enum';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import useContestInvites from '~/domains/contest/hooks/useContestInvites';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import type { SubMenuItem } from '~/domains/common/components/AppShellHeader/components/Subheader/types';

import { ContestInvitesStatusFilter } from '../../services/enums/contest-invites-status-filter.enum';

import useEntriesUserV2 from './useEntriesUserV2';


// eslint-disable-next-line no-restricted-syntax
export enum ContestTab {
  ACTIVE = 'ACTIVE',
  FINAL = 'FINAL',
  INVITES = 'INVITES',
}

const useEntriesTabsProps = () => {
  const isContestInvitesTabEnabled = useIsFeatureEnabled(FEATURE.ENABLE_CONTEST_INVITES_TAB);
  const { t } = useTranslation('contest');
  const router = useRouter();
  const { query } = router;

  const { itemsTotal } = useEntriesUserV2({
    status: ContestStatusFilter.ACTION_REQUIRED,
    limit: 3, // Aligning with other calls to check the total count, to reuse the query
    enabled: true,
  });

  const { itemsTotal: invitesTotal } = useContestInvites({
    status: ContestInvitesStatusFilter.PENDING,
  });

  const handleChange = useCallback(
    (newValue: ContestTab) => {
      // Reset the status filter when switching to final or invites tab
      if ([ContestTab.FINAL, ContestTab.INVITES].includes(newValue)) {
        delete query[URLQueryKeys.CONTEST_STATUS];
      }

      router.push({
        pathname: router.pathname,
        query: {
          ...query,
          [URLQueryKeys.CONTEST_TAB]: newValue,
        },
      });
    },
    [query, router]
  );

  const value = (router.query.contestTab as ContestTab) || ContestTab.ACTIVE;

  const tabs: SubMenuItem[] = useMemo(() => [
    {
      label: t('entries.header.tabs.active'),
      value: ContestTab.ACTIVE,
      hasBadge: itemsTotal > 0,
      onClick: handleChange,
      isActive: value === ContestTab.ACTIVE,
    },
    {
      label: t('entries.header.tabs.finalized'),
      value: ContestTab.FINAL,
      onClick: handleChange,
      isActive: value === ContestTab.FINAL,
    },
    ...(isContestInvitesTabEnabled ? [{
      label: t('entries.header.tabs.invites'),
      value: ContestTab.INVITES,
      hasBadge: invitesTotal > 0,
      onClick: handleChange,
      isActive: value === ContestTab.INVITES,
    }] : []),
  ], [isContestInvitesTabEnabled, itemsTotal, invitesTotal, value, t, handleChange]);


  return {
    tabs,
    value,
    onChange: handleChange,
  };
};

export default useEntriesTabsProps;
